import React, { useEffect, useRef, useState } from 'react'
import { MapPin, Loader, AlertCircle, Layers } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

// Google Maps types
declare global {
  interface Window {
    google: typeof google
  }
}

interface Project {
  project_id: number
  name: string
  address?: string
  city?: string
  district?: string
  latitude?: string | number
  longitude?: string | number
  status?: string
  sold_out?: boolean
}

interface GoogleMapsViewerProps {
  project: Project
  height?: string
  className?: string
}

interface MapTypeControlProps {
  currentType: google.maps.MapTypeId
  onChange: (type: google.maps.MapTypeId) => void
}

const MAP_TYPE_OPTIONS: Array<{ id: google.maps.MapTypeId; label: string }> = [
  { id: 'roadmap' as google.maps.MapTypeId, label: 'Default' },
  { id: 'satellite' as google.maps.MapTypeId, label: 'Satellite' },
  { id: 'terrain' as google.maps.MapTypeId, label: 'Terrain' },
]

function MapTypeControl({ currentType, onChange }: MapTypeControlProps) {
  return (
    <div className="pointer-events-auto flex items-center gap-1 rounded-lg border border-secondary-200 bg-white p-1 shadow-sm">
      {MAP_TYPE_OPTIONS.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
            currentType === option.id
              ? 'bg-primary-600 text-white shadow-sm'
              : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Google Maps component for displaying a single project location
 * Shows a marker for the project with valid coordinates
 */
export function GoogleMapsViewer({ 
  project, 
  height = '400px',
  className = ''
}: GoogleMapsViewerProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.Marker | google.maps.marker.AdvancedMarkerElement | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [mapType, setMapType] = useState<google.maps.MapTypeId>('roadmap' as google.maps.MapTypeId)

  // Check if project has valid coordinates
  const hasCoordinates = project.latitude && project.longitude
  const lat = project.latitude ? (typeof project.latitude === 'string' ? parseFloat(project.latitude) : project.latitude) : 0
  const lng = project.longitude ? (typeof project.longitude === 'string' ? parseFloat(project.longitude) : project.longitude) : 0

  // Fetch API key from backend
  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const response = await api.get('/geocoding/api-key')
        if (response.data.success && response.data.apiKey) {
          setApiKey(response.data.apiKey)
        } else {
          setError('Google Maps API key not available')
        }
      } catch (err) {
        console.error('Error fetching API key:', err)
        setError('Failed to load Google Maps API key')
      }
    }

    fetchApiKey()
  }, [])

  // Initialize Google Maps
  useEffect(() => {
    if (!apiKey || !hasCoordinates) return
    const initializeMap = async () => {
      if (!mapRef.current) return

      try {
        setIsLoading(true)
        setError(null)

        // Check if Google Maps is loaded
        if (!window.google || !window.google.maps || !window.google.maps.Map) {
          throw new Error('Google Maps API not fully loaded')
        }

        // Check if AdvancedMarkerElement is available, otherwise fallback to regular Marker
        const useAdvancedMarker = window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement

        // Project center
        const center = {
          lat,
          lng
        }

        // Create map
        const map = new google.maps.Map(mapRef.current, {
          center,
          zoom: 16,
          disableDefaultUI: true,
          zoomControl: true,
          fullscreenControl: true,
          gestureHandling: 'greedy',
          mapTypeId: mapType,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        })

        mapInstanceRef.current = map

        // Create marker based on available API
        let marker
        if (useAdvancedMarker) {
          // Use AdvancedMarkerElement if available
          const markerElement = document.createElement('div')
          markerElement.innerHTML = `
            <div style="
              width: 40px;
              height: 40px;
              background-color: ${getStatusColor(project.status, project.sold_out)};
              border: 3px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              cursor: pointer;
            ">
              <div style="
                width: 16px;
                height: 16px;
                background-color: white;
                border-radius: 50%;
              "></div>
            </div>
          `

          marker = new google.maps.marker.AdvancedMarkerElement({
            position: center,
            map,
            title: project.name,
            content: markerElement
          })
        } else {
          // Fallback to regular Marker
          marker = new google.maps.Marker({
            position: center,
            map,
            title: project.name,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="16" fill="${getStatusColor(project.status, project.sold_out)}" stroke="white" stroke-width="3"/>
                  <path d="M20 10c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10-4.5-10-10-10zm0 15c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5z" fill="white"/>
                </svg>
              `)}`,
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 20)
            }
          })
        }

        markerRef.current = marker

        setIsLoading(false)
      } catch (err) {
        console.error('Error initializing Google Maps:', err)
        setError('Failed to load Google Maps')
        setIsLoading(false)
        toast.error('Failed to load map')
      }
    }

    // Load Google Maps script if not already loaded
    if (!window.google || !window.google.maps) {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`
      script.async = true
      script.defer = true
      script.onload = () => {
        // Add a small delay to ensure API is fully loaded
        setTimeout(initializeMap, 100)
      }
      script.onerror = () => {
        setError('Failed to load Google Maps API')
        setIsLoading(false)
        toast.error('Failed to load Google Maps API')
      }
      document.head.appendChild(script)
    } else {
      // Add a small delay to ensure API is fully loaded
      setTimeout(initializeMap, 100)
    }

    // Cleanup
    return () => {
      if (markerRef.current) {
        // Handle both marker types
        if ('setMap' in markerRef.current) {
          // Regular Marker
          markerRef.current.setMap(null)
        } else {
          // AdvancedMarkerElement
          markerRef.current.map = null
        }
        markerRef.current = null
      }
    }
  }, [apiKey, hasCoordinates, lat, lng, mapType])

  // Get color based on project status
  const getStatusColor = (status?: string, soldOut?: boolean): string => {
    if (soldOut) return '#d64545' // Brand Danger
    switch (status?.toLowerCase()) {
      case 'sold_out': return '#d64545' // Brand Danger
      case 'active': return '#4a9686' // Brand Success
      case 'pending': return '#e68a35' // Brand Warning
      default: return '#0ea5e9' // Brand Info
    }
  }

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return
    mapInstanceRef.current.setMapTypeId(mapType)
  }, [mapType])

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-6 text-center ${className}`}>
        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <h3 className="text-sm font-medium text-red-800 mb-1">Map Unavailable</h3>
        <p className="text-xs text-red-600">{error}</p>
      </div>
    )
  }

  if (!hasCoordinates) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-6 text-center ${className}`}>
        <MapPin className="h-8 w-8 text-gray-400 mx-auto mb-2" />
        <h3 className="text-sm font-medium text-gray-800 mb-1">No Location Data</h3>
        <p className="text-xs text-gray-600">
          This project doesn't have location coordinates available for mapping.
        </p>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
          <div className="flex items-center space-x-2">
            <Loader className="h-4 w-4 animate-spin text-primary-600" />
            <span className="text-sm text-gray-600">Loading map...</span>
          </div>
        </div>
      )}
      
      <div
        ref={mapRef}
        style={{ height }}
        className="w-full rounded-lg border border-gray-200"
      />

      {!isLoading && (
        <div className="absolute top-3 right-3">
          <MapTypeControl currentType={mapType} onChange={setMapType} />
        </div>
      )}
    </div>
  )
}

// Extend Window interface for Google Maps
declare global {
  interface Window {
    google: typeof google
  }
}
